import { createQueryController } from '@tanstack/lit-query';
import {
    videoChatConfigurationQueryOptions,
    videoDynamicQuestionsQueryOptions,
    type PredefinedQuestion,
} from 'Api/chatConfiguration';
import { queryClient } from 'Api/query-client';
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-chat-mobile-trigger.scss';
import 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';

const SUMMARIZE_QUESTION = 'Summarize this video';

const CTA_QUESTION: PredefinedQuestion = { id: 31, question: 'Ask your own question', type: 'cta' };

const LOADING_SKELETON_COUNT = 3;

export interface ChatMobileTriggerSelectDetail {
    question: string;
    type: string;
}

@customElement('vm-chat-mobile-trigger')
export class VmChatMobileTrigger extends LitElement {
    static override styles = Styles;

    @property({ type: Number, attribute: 'video-id' })
    videoId?: number;

    private _chatConfigurationQuery = createQueryController(
        this,
        () => videoChatConfigurationQueryOptions(this.videoId),
        queryClient,
    );

    private _dynamicQuestionsQuery = createQueryController(
        this,
        // This is dependant on chat config because some videos have disabled VAIA
        //  and this is an expensive operation that is wasted if the chat is not enabled
        () => videoDynamicQuestionsQueryOptions(this._chatConfigurationQuery.current.data ? this.videoId : undefined),
        queryClient,
    );

    /** Anchor for the VAIA product tour, which can't reach into the shadow root by selector. */
    get firstSuggestion(): HTMLElement | null {
        return this.renderRoot.querySelector('.vm-chat-mobile-trigger__suggestion');
    }

    private _select(question: PredefinedQuestion) {
        this.dispatchEvent(
            new CustomEvent<ChatMobileTriggerSelectDetail>('vm-chat-mobile-trigger:select', {
                detail: { question: question.question, type: question.type },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _renderQuestion(question: PredefinedQuestion, index: number) {
        return html`
            <vm-button
                type="button"
                variant="outline"
                color-scheme="neutral"
                size="sm"
                id=${index + 1}
                class="vm-chat-mobile-trigger__suggestion"
                data-type=${question.type}
                @click=${() => this._select(question)}
            >
                ${question.question}
            </vm-button>
        `;
    }

    private _renderSuggestions() {
        const predefinedQuestions = this._chatConfigurationQuery.current.data?.predefined_questions ?? [];
        const summarize = predefinedQuestions.filter((question) => question.question === SUMMARIZE_QUESTION);
        const { status, data } = this._dynamicQuestionsQuery.current;

        if (status === 'pending') {
            return [
                ...summarize.map((question, index) => this._renderQuestion(question, index)),
                ...Array.from(
                    { length: LOADING_SKELETON_COUNT },
                    () => html`<div class="vm-chat-mobile-trigger__skeleton" aria-hidden="true"></div>`,
                ),
            ];
        }

        const dynamicQuestions = (data?.questions ?? []).map((question) => ({
            id: question.id,
            question: question.question,
            type: question.question_source,
        }));

        // Errored or empty dynamic questions fall back to the full config list
        const questions = dynamicQuestions.length ? [...summarize, ...dynamicQuestions] : predefinedQuestions;

        return [...questions, CTA_QUESTION].map((question, index) => this._renderQuestion(question, index));
    }

    override render() {
        return html`<div
            class="vm-chat-mobile-trigger__suggestions"
            aria-busy=${this._dynamicQuestionsQuery.current.isPending}
        >
            <vm-icon size="sm" name="sparkle"></vm-icon>
            ${this._renderSuggestions()}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-chat-mobile-trigger': VmChatMobileTrigger;
    }
    interface HTMLElementEventMap {
        'vm-chat-mobile-trigger:select': CustomEvent<ChatMobileTriggerSelectDetail>;
    }
    interface DocumentEventMap {
        'vm-chat-mobile-trigger:select': CustomEvent<ChatMobileTriggerSelectDetail>;
    }
}
